import { AxiosError, Vendor } from "@shared/index";
import { AxiosRequestConfig, isAxiosError } from "axios";
import { axios } from '@client-shared/services/api';
import { vendorLocalStorageKeys } from '../vendor/types';
import { sfdcLocalStorageKeys } from "./types";

// ====================================================================================================================
// helper functions ----------------------------------//
// ====================================================================================================================

// set the request interceptor
const setRequestInterceptor = () => {
  return axios.interceptors.request.use((request) => {
    const idToken = localStorage.getItem(sfdcLocalStorageKeys.sfdcIdToken);
    if (idToken) {
      request.headers.Authorization = `Bearer ${idToken}`;
    }

    if (process.env.REACT_APP_LOCAL?.toLowerCase() === "true" && !request._retry && getVendorKey()) {
      request.params = { ...request.params, vendorKey: getVendorKey() };
    }

    console.log(`${request.method?.toUpperCase()} ${request.baseURL}${request.url}`);
    return request;
  });
};

let isFetchingToken = false;

// set the response interceptor
const setResponseInterceptor = (handleError: () => void, handleRefreshToken: (token: string) => Promise<void>) => {
  return axios.interceptors.response.use(
    (response) => {
      console.log(`${response.status} ${response.statusText}`);
      return response;
    },
    async (error) => {
      if (isAxiosError(error) && error.response?.status === 403) {
        return await handleRequestError(error as AxiosError, handleError, handleRefreshToken);
      }
      let toReject: Error;

      if (isAxiosError(error)) {
        toReject = new Error(error.message);
        Object.assign(toReject, error); // Preserve AxiosError props (e.g., config, code, response)
      } else if (error instanceof Error) {
        toReject = error;
      } else {
        toReject = new Error(String(error)); // Fallback for unknown errors
      }
      throw toReject;
    }
  );
};

const handleRequestError = async (error: AxiosError, handleError: () => void, handleRefreshToken: (token: string) => Promise<void>) => {
  const originalRequest = error.config as AxiosRequestConfig;
  const refreshToken = localStorage.getItem(sfdcLocalStorageKeys.sfdcRefreshToken);

  // If there's no refresh token or the request has already been retried, handle the error
  if (!refreshToken || originalRequest._retry) {
    handleError();
    return;
  }
  originalRequest._retry = true;

  // If a token is being fetched, queue the request
  if (isFetchingToken) {
    return queueFailedRequest(originalRequest);
  }

  // Handle token refresh and retry the original request
  return await attemptTokenRefreshAndRetry(originalRequest, refreshToken, handleRefreshToken, handleError);
};

const queueFailedRequest = (originalRequest: AxiosRequestConfig) => {
  return new Promise((resolve, reject) => {
    failedQueue.push({
      resolve: () => {
        resolve(axios.request(originalRequest));
      },
      reject,
    });
  });
};

const attemptTokenRefreshAndRetry = async (
  originalRequest: AxiosRequestConfig,
  refreshToken: string,
  handleRefreshToken: (token: string) => Promise<void>,
  handleError: () => void
) => {
  isFetchingToken = true;
  try {
    await handleRefreshToken(refreshToken);
    processQueue();
    return axios.request(originalRequest);
  } catch (refreshError) {
    processQueue(refreshError as Error);
    handleError();

    let toReject: Error;

    if (isAxiosError(refreshError)) {
      // Create a new Error that wraps the AxiosError but is still an instance of Error
      toReject = new Error(refreshError.message);
      Object.assign(toReject, refreshError); // preserve original AxiosError properties
    } else {
      toReject = refreshError as Error;
    }

    throw toReject;
  } finally {
    isFetchingToken = false;
  }
};

const ejectRequestInterceptor = (requestInterceptor: number) => {
  axios.interceptors.request.eject(requestInterceptor);
};

const ejectResponseInterceptor = (responseInterceptor: number) => {
  axios.interceptors.response.eject(responseInterceptor);
};

const failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

const processQueue = (error?: Error | null) => {
  while (failedQueue.length > 0) {
    const nextPromise = failedQueue.shift();
    if (error) {
      nextPromise?.reject(error);
    } else {
      nextPromise?.resolve();
    }
  }
};

const getVendorKey = () => {
  const vendorString = localStorage.getItem(vendorLocalStorageKeys.vendor);
  const vendor: Vendor | undefined = vendorString ? JSON.parse(vendorString) : undefined;
  return vendor?.vendorKey;
};

// ====================================================================================================================
// UseAxiosInterceptor hook ----------------------------------//
// ====================================================================================================================

export type SetAllInterceptors = (
  handleSetRequestInterceptor: (set: () => number) => void,
  handleSetResponseInterceptor: (set: (handleError: () => void, refreshToken: (token: string) => Promise<void>) => number) => void
) => void;

export type EjectAllInterceptors = (requestInterceptor: number | null, responseInterceptor: number | null) => void;

const useSfdcAuthInterceptor = () => {
  const handleSetAllInterceptors: SetAllInterceptors = (handleSetRequestInterceptor, handleSetResponseInterceptor) => {
    handleSetRequestInterceptor(setRequestInterceptor);
    handleSetResponseInterceptor(setResponseInterceptor);
  };

  const handleEjectAllInterceptors: EjectAllInterceptors = (requestInterceptor, responseInterceptor) => {
    if (requestInterceptor) {
      ejectRequestInterceptor(requestInterceptor);
    }
    if (responseInterceptor) {
      ejectResponseInterceptor(responseInterceptor);
    }
  };

  return { handleSetAllInterceptors, handleEjectAllInterceptors };
};

export default useSfdcAuthInterceptor;
